import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/ui';
import { Icon } from '@/icons';
import { useViewport } from '@/hooks';
import { media } from '@/theme/media';
import { SECTIONS, SECTION_ICONS } from './meta';
import { SectionNavMobile } from '@/app/shell/SectionNavMobile';
import { CouncilSidebar } from './components/CouncilSidebar';
import { CouncilHeader } from './components/CouncilHeader';
import { OverviewSection } from './sections/OverviewSection';
import { CaseQueueSection } from './sections/CaseQueueSection';
import { HumanReviewSection } from './sections/HumanReviewSection';
import { VotingBoardSection } from './sections/VotingBoardSection';
import { AppealsSection } from './sections/AppealsSection';
import { AuditLogSection } from './sections/AuditLogSection';
import { CouncilFeedbackSection } from './sections/CouncilFeedbackSection';
import { CouncilComingSoon } from './sections/CouncilComingSoon';

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.color.bgRoot};
`;

const Bar = styled.div`
  padding: 16px 24px;
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 24px;
  /* Full-bleed canvas, matches SettingsLayout. Was capped at 1320px + centred,
     which left ~300px of dead margin either side on a 1920px display. */
  flex: 1 0 auto;
  padding: 0 24px 24px;
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Pane = styled.div`
  min-width: 0;
`;

/** Desktop sidebar — always in DOM; CSS hides on mobile. */
const SidebarWrap = styled.div`
  ${media.mobile`display: none;`}
`;

/** Mobile section-switcher — rendered by JS when mobile; CSS also hides on tablet/desktop. */
const MobileNavWrap = styled.div`
  margin-bottom: 16px;
  ${media.tabletUp`display: none;`}
`;

function SectionContent({ activeId }: { activeId: string }) {
  switch (activeId) {
    case 'overview':     return <OverviewSection />;
    case 'case-queue':   return <CaseQueueSection />;
    case 'human-review': return <HumanReviewSection />;
    case 'voting-board': return <VotingBoardSection />;
    case 'appeals':      return <AppealsSection />;
    case 'audit-log':    return <AuditLogSection />;
    case 'feedback':     return <CouncilFeedbackSection />;
    default:             return <CouncilComingSoon sectionId={activeId} />;
  }
}

export function CouncilLayout({ activeId }: { activeId: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const viewport = useViewport();

  const mobileSections = SECTIONS.map((s) => ({
    id: s.id,
    label: s.label,
    icon: SECTION_ICONS[s.id],
  }));

  function handleBack() {
    if (location.key === 'default') navigate('/');
    else navigate(-1);
  }

  function handleSectionSelect(id: string) {
    navigate(`/council/${id}`, { replace: true });
  }

  return (
    <Page>
      <Bar>
        <Button variant="ghost" onClick={handleBack}>
          <Icon name="arrow-left" size={16} /> Back
        </Button>
      </Bar>
      <Body>
        {/* Sidebar: always in DOM; CSS hides on mobile */}
        <SidebarWrap data-testid="council-sidebar-wrap">
          <CouncilSidebar activeId={activeId} />
        </SidebarWrap>
        <Pane>
          {/* Mobile nav: JS-gated for mobile; CSS also hides on tablet/desktop */}
          {viewport === 'mobile' && (
            <MobileNavWrap data-testid="council-mobile-nav-wrap">
              <SectionNavMobile
                sections={mobileSections}
                activeId={activeId}
                onSelect={handleSectionSelect}
              />
            </MobileNavWrap>
          )}
          <CouncilHeader />
          <SectionContent activeId={activeId} />
        </Pane>
      </Body>
    </Page>
  );
}
